import { toast } from "sonner";
import { z } from "zod/v3";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";

import { settingsApi } from "@workspace/settings";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { Input } from "@workspace/ui/components/input";
import {
	Choicebox,
	ChoiceboxItem,
	ChoiceboxItemContent,
	ChoiceboxItemDescription,
	ChoiceboxItemHeader,
	ChoiceboxItemIndicator,
	ChoiceboxItemTitle,
} from "@workspace/ui/components/kibo-ui/choicebox";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

const PaymentProviderSchema = z.object({
	type: z.string().min(1, "Please select a payment provider"),
	accountNumber: z
		.string()
		.min(1, "Account/Provider ID is required")
		.max(100, "Account/Provider ID must be less than 100 characters")
		.optional()
		.or(z.literal("")),
	paymentMethods: z.array(z.string()),
});

export type PaymentProviderFormValues = z.infer<typeof PaymentProviderSchema>;

const paymentProviders = [
	{
		id: "manual",
		label: "Manual / Bank Transfer",
		description: "Cash, bank transfers, invoicing",
		hasAccount: false,
		accountPlaceholder: "Bank account or reference",
		paymentMethods: ["cash"],
	},
	{
		id: "stripe",
		label: "Stripe",
		description: "Terminal, Credit cards, Apple Pay, Google Pay",
		hasAccount: true,
		accountPlaceholder: "sk_live_... or acct_...",
		paymentMethods: ["cash", "card"],
	},
];

export function PaymentProviderCard({ tenant }: { tenant: string }) {
	const settingsQuery = useSuspenseQuery({
		...settingsApi.getOptions<{
			paymentProvider: {
				type?: string;
				accountNumber?: string;
				paymentMethods?: string[];
			};
		}>(tenant, "payments"),
	});

	const paymentProviderMutation = useMutation({
		...settingsApi.postOptions(tenant, "payments/provider"),
		onSuccess: (response) => {
			if (response.success) {
				toast.success("Payment provider updated");
			} else {
				toast.error(response.error || "Failed to update payment provider");
			}
		},
		onError: (error: unknown) => {
			const message =
				typeof error === "object" && error !== null && "message" in error
					? (error as { message?: string }).message
					: undefined;
			toast.error(message || "Failed to update payment provider");
		},
		async onSettled(data, _error, _variables, _onMutateResult, context) {
			if (data?.success)
				await context.client.invalidateQueries({ queryKey: [tenant] });
		},
	});

	const form = useAppForm({
		defaultValues: {
			...settingsQuery.data.paymentProvider,
			type: settingsQuery.data.paymentProvider.type || "manual",
			paymentMethods: settingsQuery.data.paymentProvider.paymentMethods || [],
		} as Partial<PaymentProviderFormValues>,
		validationLogic: revalidateLogic(),
		validators: {
			onDynamic: PaymentProviderSchema,
		},
		onSubmit: ({ value }) => {
			return paymentProviderMutation.mutate({ ...value, type: value.type });
		},
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex h-8 items-center justify-between">
					Payment Provider Settings
				</CardTitle>
				<CardDescription>
					Configure your payment processing provider for this tenant
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="w-full space-y-6">
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-6"
					>
						<form.AppField name="type">
							{(field) => (
								<field.FormItem>
									<field.FormControl>
										<Choicebox
											defaultValue="1"
											className="w-full lg:flex"
											value={field.state.value || ""}
											onValueChange={(value: string) => {
												field.handleChange(value);
												// Clear account number when provider changes
												form.setFieldValue("accountNumber", "");
												const selectedProvider = paymentProviders.find(
													(p) => p.id === value,
												);
												form.setFieldValue(
													"paymentMethods",
													selectedProvider?.paymentMethods,
												);
											}}
										>
											{paymentProviders.map((option) => (
												<ChoiceboxItem
													className="min-w-[250px]"
													key={option.id}
													value={option.id}
												>
													<ChoiceboxItemHeader>
														<ChoiceboxItemTitle>
															{option.label}
														</ChoiceboxItemTitle>
														<ChoiceboxItemDescription>
															{option.description}
														</ChoiceboxItemDescription>
													</ChoiceboxItemHeader>
													<ChoiceboxItemContent>
														<ChoiceboxItemIndicator />
													</ChoiceboxItemContent>
												</ChoiceboxItem>
											))}
										</Choicebox>
									</field.FormControl>
									<field.FormDescription>
										Choose your payment processing provider
									</field.FormDescription>
									<field.FormMessage />
								</field.FormItem>
							)}
						</form.AppField>

						<form.Subscribe selector={(state) => state.values.type}>
							{(provider) => {
								if (!provider) return null;

								const selectedProvider = paymentProviders.find(
									(p) => p.id === provider,
								);

								if (!selectedProvider || !selectedProvider.hasAccount)
									return null;

								return (
									<form.AppField name="accountNumber">
										{(field) => (
											<field.FormItem>
												<field.FormLabel>
													{selectedProvider.label || "Provider"} Account/ID
												</field.FormLabel>
												<field.FormControl>
													<Input
														placeholder={
															selectedProvider.accountPlaceholder ||
															"Enter account ID or credentials"
														}
														value={field.state.value || ""}
														onChange={(e) => field.handleChange(e.target.value)}
													/>
												</field.FormControl>
												<field.FormDescription>
													Enter your {selectedProvider.label} account identifier
													or API key
												</field.FormDescription>
												<field.FormMessage />
											</field.FormItem>
										)}
									</form.AppField>
								);
							}}
						</form.Subscribe>

						<form.Subscribe
							selector={(state) => [
								state.canSubmit,
								state.isSubmitting,
								state.isDirty,
							]}
						>
							{([canSubmit, isSubmitting, isDirty]) =>
								isDirty ? (
									<div className="flex justify-end gap-3">
										<Button
											type="button"
											variant="outline"
											onClick={() => form.reset()}
											disabled={isSubmitting}
										>
											Cancel
										</Button>

										<Button type="submit" disabled={!canSubmit || isSubmitting}>
											{isSubmitting ? "Saving..." : "Save Changes"}
										</Button>
									</div>
								) : null
							}
						</form.Subscribe>
					</form>
				</div>
			</CardContent>
		</Card>
	);
}

export default PaymentProviderCard;
